import React from "react";
import { useNavigate } from "react-router-dom";
import PageMeta from "../components/PageMeta";
import PageShell from "../components/PageShell";
import {
  BookingCTA,
  CTAButton,
  DemoForm,
  FeatureCard,
  PersonaSection,
  PrincipleCard,
  ProductMockupPanel,
  Section,
  WorkflowStep,
} from "../components/MarketingPrimitives";
import {
  credibilityCards,
  croBullets,
  differentiationCards,
  executionCapabilities,
  founderBullets,
  heroCapabilities,
  presalesExecutionCards,
  principles,
  problemPoints,
  revenueQuestions,
  signalCapabilities,
  signalToExecutionWorkflow,
  valueTeamBullets,
  workflowColumns,
} from "../content/siteContent";

const HomePage = () => {
  const navigate = useNavigate();

  return (
    <PageShell>
      <PageMeta
        title="BestSalesCoach.ai | AI GTM Execution Platform"
        description="BestSalesCoach.ai helps B2B technology teams turn buyer signals into visible opportunities, stakeholder maps, MEDDICC guidance, technical validation plans, and next-best actions across Sales, PreSales, RevOps, and Customer Success."
        canonicalPath="/"
      />
      <Section id="hero" tone="hero" className="hero-section">
        <div className="hero-grid">
          <div>
            <p className="marketing-eyebrow">AI GTM execution platform for CROs and founders</p>
            <h1>Turn Buyer Signals into Revenue Execution Evidence</h1>
            <p className="marketing-hero-copy">
              BestSalesCoach.ai helps CROs, founders, and GTM teams connect ICP, account signals, stakeholder intelligence, MEDDICC discipline, technical validation, and next-best actions into one repeatable GTM execution system.
            </p>
            <div className="hero-actions">
              <BookingCTA
                eventName="book_demo_click"
                sourcePage="home"
                ctaClicked="book_20_min_demo"
                requestedOffer="BestSalesCoach.ai Platform Demo"
                buyerType="General"
                urlType="demo"
              />
              <CTAButton variant="secondary" onClick={() => navigate("/home#platform-workflow")}>
                See How It Works
              </CTAButton>
            </div>
            <div className="capability-strip">
              {heroCapabilities.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
            <p className="marketing-body-copy hero-narrative">
              BestSalesCoach.ai turns account signals into qualified opportunity action: who to
              engage, what evidence is missing, what risk exists, and what the team should do next.
            </p>
          </div>
          <ProductMockupPanel />
        </div>
      </Section>

      <Section
        id="problem"
        eyebrow="Why pipeline quality slips"
        title="Revenue teams do not lose deals only because of product fit. They lose because execution breaks down."
        description="The highest cost in B2B GTM is not missing data. It is failing to connect signals, context, stakeholders, and execution before momentum is lost."
        tone="light"
      >
        <div className="problem-grid">
          {problemPoints.map((point) => (
            <article key={point} className="problem-card">
              <span className="problem-dot" aria-hidden="true" />
              <p>{point}</p>
            </article>
          ))}
        </div>
      </Section>

      <Section
        id="signal-execution-workflow"
        eyebrow="Signal-to-execution workflow"
        title="From buyer signal to opportunity action"
        description="BestSalesCoach.ai connects the signals, evidence, stakeholders, methodology, and actions that usually sit across disconnected tools."
        tone="dark"
      >
        <div className="workflow-sequence">
          {signalToExecutionWorkflow.map((step, index) => (
            <article key={step} className="workflow-sequence-card">
              <span className="workflow-sequence-step">{index + 1}</span>
              <h3>{step}</h3>
            </article>
          ))}
        </div>
      </Section>

      <Section
        id="principles"
        eyebrow="Revenue execution principles"
        title="Built on Revenue Execution Principles"
        description="The platform is designed around the mechanics that improve account prioritisation, pipeline quality, team learning, and forecast reliability."
        tone="dark"
      >
        <div className="marketing-grid marketing-grid-three">
          {principles.map((item) => (
            <PrincipleCard key={item.title} {...item} />
          ))}
        </div>
      </Section>

      <Section
        id="platform-workflow"
        eyebrow="One execution layer"
        title="One AI Execution Layer Across Leads, Opportunities, Accounts, and Conversations"
        description="Bring CRM data, meeting intelligence, account signals, and stakeholder context into one review loop that helps teams act, coach, and inspect with more precision."
        tone="dark"
      >
        <div className="marketing-grid marketing-grid-four">
          {workflowColumns.map((column) => (
            <WorkflowStep key={column.title} {...column} />
          ))}
        </div>
      </Section>

      <Section
        id="sales-presales-execution"
        eyebrow="Sales and PreSales"
        title="Built for Sales and PreSales Execution"
        description="Align Sales and PreSales before the demo, not after the deal is already at risk."
        tone="light"
      >
        <div className="marketing-section-header">
          <p className="marketing-section-description">
            BestSalesCoach.ai helps teams turn discovery signals, technical gaps, stakeholder context,
            demo objectives, and proof-of-value criteria into shared execution plans for AEs, SEs,
            managers, and customer-facing teams.
          </p>
          <p className="marketing-section-description">
            Most revenue platforms stop at seller activity or forecast inspection. BestSalesCoach.ai
            brings PreSales and technical validation into the same execution loop.
          </p>
        </div>
        <div className="marketing-grid marketing-grid-three">
          {presalesExecutionCards.map((card) => (
            <FeatureCard key={card.title} {...card} />
          ))}
        </div>
      </Section>

      <Section
        id="revenue-questions"
        eyebrow="Revenue questions"
        title="Answer the questions revenue leaders ask every week"
        description="BestSalesCoach.ai helps teams move from opinion-based updates to evidence-backed GTM execution."
        tone="dark"
      >
        <div className="marketing-grid marketing-grid-two">
          {revenueQuestions.map((question) => (
            <article key={question} className="marketing-card question-card">
              <h3>{question}</h3>
            </article>
          ))}
        </div>
      </Section>

      <Section
        id="signal-opportunity"
        eyebrow="Platform capabilities"
        title="Signal-to-Opportunity"
        description="Move from raw signals and scattered context to prioritised accounts, shared briefs, and clearer action."
        tone="light"
      >
        <div className="marketing-grid marketing-grid-four">
          {signalCapabilities.map((capability) => (
            <FeatureCard key={capability.title} {...capability} />
          ))}
        </div>
      </Section>

      <Section
        id="capabilities"
        eyebrow="Platform capabilities"
        title="Opportunity Execution"
        description="Keep the platform grounded in human-in-the-loop execution: AI identifies, recommends, and summarises; people approve, coach, and learn."
        tone="dark"
      >
        <div className="marketing-grid marketing-grid-four">
          {executionCapabilities.map((capability) => (
            <FeatureCard key={capability.title} {...capability} />
          ))}
        </div>
      </Section>

      <Section
        id="for-cros"
        eyebrow="Buyer path"
        title="For CROs: Know Which Accounts and Deals Need Action Before They Hit the Forecast"
        description="BestSalesCoach.ai helps CROs inspect pipeline quality, identify signal-to-action gaps, expose execution risk, and coach managers and reps with evidence-backed AI insights."
        tone="dark"
      >
        <PersonaSection
          eyebrow="For CROs"
          title="Inspect pipeline quality with more signal and less noise"
          description="Use AI-driven signal, account, and deal reviews to expose risk, tighten inspection quality, and help managers coach what matters."
          bullets={croBullets}
          ctaLabel="Book a CRO Demo"
          bookingProps={{
            eventName: "cro_demo_click",
            sourcePage: "for_cros",
            ctaClicked: "cro_demo",
            requestedOffer: "CRO Pipeline Review",
            buyerType: "CRO",
            urlType: "cro",
          }}
        />
      </Section>

      <Section
        id="for-founders"
        eyebrow="Founder path"
        title="For Founders: Turn Founder-Led Sales into a Repeatable Signal-to-Revenue System"
        description="BestSalesCoach.ai helps founders focus on the right accounts, validate ICP, qualify opportunities properly, prepare for enterprise conversations, and turn customer learning into a repeatable GTM motion."
        tone="dark"
      >
        <PersonaSection
          eyebrow="For Founders"
          title="Scale what works before complexity starts to compound"
          description="Capture the judgment behind good founder-led selling and convert it into a repeatable system for prioritisation, qualification, and enterprise deal progression."
          bullets={founderBullets}
          ctaLabel="Book a Founder Demo"
          bookingProps={{
            eventName: "founder_demo_click",
            sourcePage: "for_founders",
            ctaClicked: "founder_demo",
            requestedOffer: "Founder-Led GTM System",
            buyerType: "Founder",
            urlType: "founder",
          }}
        />
      </Section>

      <Section
        id="product-proof"
        eyebrow="Product proof"
        title="See How BestSalesCoach.ai Turns Signal into Opportunity Action"
        description="This example shows how the platform turns scattered account, stakeholder, signal, and opportunity context into a structured execution view for sellers, managers, founders, and CROs."
        tone="light"
      >
        <div className="proof-grid">
          <article className="marketing-card proof-summary-card">
            <p className="marketing-kicker">Example account and opportunity</p>
            <h3>Acme Enterprise Expansion</h3>
            <div className="proof-metrics">
              <div>
                <span>ICP fit</span>
                <strong>Strong</strong>
              </div>
              <div>
                <span>Buying signal</span>
                <strong>Active</strong>
              </div>
              <div>
                <span>Forecast risk</span>
                <strong>Medium-high</strong>
              </div>
              <div>
                <span>Champion strength</span>
                <strong>Weak</strong>
              </div>
              <div>
                <span>Economic buyer</span>
                <strong>Not confirmed</strong>
              </div>
            </div>
          </article>

          <div className="proof-details-grid">
            <article className="marketing-card">
              <h3>MEDDICC Completeness</h3>
              <ul className="marketing-bullet-list compact">
                <li>Metrics: Business impact not quantified</li>
                <li>Decision process: Unclear</li>
                <li>Paper process: Not captured</li>
              </ul>
            </article>
            <article className="marketing-card">
              <h3>Missing Evidence</h3>
              <ul className="marketing-bullet-list compact">
                <li>No CFO sponsor linked to the initiative</li>
                <li>Champion has interest but limited political access</li>
                <li>No validated timeline tied to buying event</li>
              </ul>
            </article>
            <article className="marketing-card">
              <h3>Recommended Next Actions</h3>
              <p>Secure executive discovery with CFO sponsor before committing this deal.</p>
              <p className="marketing-muted">
                Coach recommendation: Validate decision criteria, confirm the economic buyer, and tighten paper process before forecast inclusion.
              </p>
            </article>
          </div>
        </div>
      </Section>

      <Section
        id="credibility"
        eyebrow="Built for complex B2B technology sales"
        title="Designed from real GTM execution, not generic sales coaching"
        description="BestSalesCoach.ai is built around the realities of complex B2B technology sales: multi-stakeholder buying, Sales and PreSales collaboration, MEDDICC discipline, technical validation, account strategy, and customer-facing follow-through."
        tone="light"
      >
        <div className="marketing-grid marketing-grid-four">
          {credibilityCards.map((card) => (
            <FeatureCard key={card.title} {...card} />
          ))}
        </div>
      </Section>

      <Section
        id="different-by-design"
        eyebrow="Different by design"
        title="Not another call recorder, CRM dashboard, or sales coaching chatbot"
        description="BestSalesCoach.ai focuses on the execution gap between signal, stakeholder context, methodology, technical validation, and action."
        tone="dark"
      >
        <div className="marketing-grid marketing-grid-four">
          {differentiationCards.map((card) => (
            <FeatureCard key={card.title} {...card} />
          ))}
        </div>
      </Section>

      <Section
        id="value-creation"
        eyebrow="Value creation teams"
        title="Built for Value Creation Teams"
        description="Top-performing GTM teams do more than sell. They collaborate, build champions, create urgency, align around customer outcomes, and execute with discipline. BestSalesCoach.ai operationalises this behaviour with AI GTM execution intelligence."
        tone="dark"
      >
        <div className="pill-row">
          {valueTeamBullets.map((item) => (
            <div key={item} className="marketing-pill">
              {item}
            </div>
          ))}
        </div>
      </Section>

      <Section
        id="book-demo"
        eyebrow="Get started"
        title="Ready to Turn Signals into Better GTM Execution?"
        description="Book a 20-minute walkthrough and see how BestSalesCoach.ai connects account signals, pipeline context, stakeholder intelligence, MEDDICC gaps, technical validation, and next-best actions."
        tone="light"
      >
        <div className="final-cta-grid">
          <div>
            <article className="marketing-card">
              <p className="marketing-eyebrow">How We Start</p>
              <h3>Begin with one workflow, not a large rollout</h3>
              <p className="marketing-body-copy">
                Most teams start by choosing one priority GTM workflow: signal-to-opportunity,
                MEDDICC execution, Sales and PreSales alignment, founder-led sales, or pipeline
                quality review. We use that workflow to show how BestSalesCoach.ai turns scattered
                signals and account context into clearer execution.
              </p>
              <p className="marketing-muted">
                Commercial options depend on workflow scope, team size, integrations, and whether
                you need product access, GTM engineering support, or both.
              </p>
            </article>
            <div className="marketing-section-header" style={{ marginTop: 24, marginBottom: 0 }}>
              <h3>See a focused product walkthrough for your revenue motion</h3>
              <p className="marketing-body-copy">
                We can tailor the conversation around account prioritisation, signal workflows,
                forecast confidence, founder-led sales, MEDDICC discipline, champion building, or
                account strategy.
              </p>
            </div>
          </div>
          <DemoForm />
        </div>
      </Section>
    </PageShell>
  );
};

export default HomePage;
